import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Toggle from '../components/Toggle';

const PricingCard = ({
  plan,
  price,
  originalPrice,
  description,
  features,
  isPopular = false,
  buttonText = "Get Started",
  buttonStyle = "primary",
  billingType,
  onSelect
}) => (
  <div className={`relative bg-white rounded-2xl p-8 border transition-all hover:shadow-lg hover:-translate-y-1 flex flex-col ${
    isPopular ? 'border-df-gold shadow-lg' : 'border-gray-200'
  }`}>
    {isPopular && (
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
        <span className="bg-df-gold text-white px-6 py-2 rounded-full text-sm font-semibold">
          Most Popular
        </span>
      </div>
    )}

    <div className="text-center mb-8">
      <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan}</h3>
      <p className="text-slate-600 mb-6">{description}</p>

      <div className="mb-4">
        {price === 'Free' ? (
          <div className="text-4xl font-bold text-slate-900">Free</div>
        ) : (
          <div className="flex items-center justify-center">
            <span className="text-4xl font-bold text-slate-900">${price}</span>
            <span className="text-slate-600 ml-2">/{billingType === 'monthly' ? 'month' : 'year'}</span>
          </div>
        )}
        {originalPrice && (
          <div className="text-slate-500 line-through text-lg">
            ${originalPrice}/{billingType === 'monthly' ? 'month' : 'year'}
          </div>
        )}
      </div>
    </div>

    <ul className="space-y-4 mb-8 flex-grow">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start">
          <span className="text-df-gold mr-3 mt-0.5 flex-shrink-0">✓</span>
          <span className="text-slate-700">{feature}</span>
        </li>
      ))}
    </ul>

    <button
      onClick={onSelect}
      className={`w-full py-4 px-6 rounded-xl font-semibold transition-all ${
        buttonStyle === 'primary'
          ? 'bg-slate-900 text-white hover:bg-slate-800 hover:shadow-lg hover:-translate-y-1'
          : buttonStyle === 'popular'
          ? 'bg-df-gold text-white hover:bg-df-gold-dark hover:shadow-lg hover:-translate-y-1'
          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-2 border-slate-200'
      }`}
    >
      {buttonText}
    </button>
  </div>
);

const Pricing = () => {
  const [billingType, setBillingType] = useState('monthly');
  const navigate = useNavigate();

  const handleGetStarted = (planType) => {
    navigate(`/signup?plan=${planType}`);
  };

  const plans = [
    {
      plan: "Essentials",
      price: "Free",
      description: "Get started and see if it's right for you",
      features: [
        "Basic directory profile",
        "1 website template",
        "Up to 3 bookings/month"
      ],
      buttonText: "Start Free",
      buttonStyle: "secondary"
    },
    {
      plan: "Booking & Directory",
      price: billingType === 'monthly' ? '29' : '246',
      originalPrice: billingType === 'annual' ? '348' : null,
      description: "Bookings, payments, and visibility",
      features: [
        "Everything in Essentials, plus:",
        "Enhanced directory profile",
        "Full booking & scheduling (unlimited)",
        "Integrated payments (keep 100% of revenue)",
        "All website templates",
        "Calendar sync (Google Calendar)"
      ],
      buttonText: "Get Booking & Directory",
      buttonStyle: "primary"
    },
    {
      plan: "Professional",
      price: billingType === 'monthly' ? '49' : '490',
      originalPrice: billingType === 'annual' ? '588' : null,
      description: "Everything you need to run your practice",
      features: [
        "Everything in Booking & Directory, plus:",
        "Client management",
        "Client messaging",
        "Guided Practice Setup",
        "Verification badge",
        "AI Intelligent Dashboard"
      ],
      isPopular: true,
      buttonText: "Go Professional",
      buttonStyle: "popular"
    },
    {
      plan: "Premier",
      price: billingType === 'monthly' ? '99' : '990',
      originalPrice: billingType === 'annual' ? '1188' : null,
      description: "AI-powered tools for serious practitioners",
      features: [
        "Everything in Professional, plus:",
        "AI Session Prep",
        "AI Session Notes (auto-transcription)",
        "Priority directory placement",
        "Early access to new features"
      ],
      buttonText: "Go Premier",
      buttonStyle: "primary"
    }
  ];

  const comparisonFeatures = [
    { feature: "Directory profile", essentials: "Basic", booking: "Enhanced", professional: "Enhanced", premier: "Priority placement" },
    { feature: "Website templates", essentials: "1", booking: "All", professional: "All", premier: "All" },
    { feature: "Bookings", essentials: "Up to 3/month", booking: "Unlimited", professional: "Unlimited", premier: "Unlimited" },
    { feature: "Integrated payments", essentials: "—", booking: "✓ (keep 100%)", professional: "✓ (keep 100%)", premier: "✓ (keep 100%)" },
    { feature: "Calendar sync", essentials: "—", booking: "✓", professional: "✓", premier: "✓" },
    { feature: "Client management", essentials: "—", booking: "—", professional: "✓", premier: "✓" },
    { feature: "Client messaging", essentials: "—", booking: "—", professional: "✓", premier: "✓" },
    { feature: "Guided Practice Setup", essentials: "—", booking: "—", professional: "✓", premier: "✓" },
    { feature: "Verification badge", essentials: "—", booking: "—", professional: "✓", premier: "✓" },
    { feature: "AI Intelligent Dashboard", essentials: "—", booking: "—", professional: "✓", premier: "✓" },
    { feature: "AI Session Prep", essentials: "—", booking: "—", professional: "—", premier: "✓" },
    { feature: "AI Session Notes", essentials: "—", booking: "—", professional: "—", premier: "✓" },
    { feature: "Early access to new features", essentials: "—", booking: "—", professional: "—", premier: "✓" },
  ];

  return (
    <>
      <Helmet>
        <title>Pricing — Divine Frameworks</title>
        <meta name="description" content="Simple, transparent pricing for spiritual practitioners. Start free, upgrade when you're ready. No transaction fees — keep 100% of your booking revenue." />
        <meta property="og:title" content="Pricing — Divine Frameworks" />
        <meta property="og:description" content="Simple pricing for spiritual practitioners. Start free and keep 100% of your booking revenue." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://divineframeworks.com/pricing" />
        <meta property="og:site_name" content="Divine Frameworks" />
      </Helmet>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <h1 className="text-5xl font-bold text-slate-900 mb-6">
            Simple Pricing, No Surprises
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            You keep every dollar you earn from clients. Plans designed for practitioners at every stage of their journey.
          </p>

          {/* Seeker note */}
          <div style={{ background: '#f8f5ff', padding: '12px 24px', borderRadius: '8px', textAlign: 'center', margin: '16px auto 0', maxWidth: '480px' }}>
            <span style={{ fontSize: '14px', fontWeight: 400, color: '#666' }}>
              Looking for a practitioner? Browsing and booking is always free.
            </span>
            <a
              href="/directory"
              onClick={(e) => { e.preventDefault(); navigate('/directory'); }}
              style={{ fontSize: '14px', fontWeight: 600, color: '#7c3aed', textDecoration: 'none', marginLeft: '6px', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >Browse practitioners →</a>
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center">
            <span className={`mr-3 ${billingType === 'monthly' ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
              Monthly
            </span>
            <Toggle
              enabled={billingType === 'annual'}
              onChange={(val) => setBillingType(val ? 'annual' : 'monthly')}
            />
            <span className={`ml-3 ${billingType === 'annual' ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
              Annual
            </span>
            {billingType === 'annual' && (
              <span className="ml-3 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                Save 15%
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {plans.map((plan, index) => (
            <PricingCard
              key={index}
              {...plan}
              billingType={billingType}
              onSelect={() => handleGetStarted(plan.plan.toLowerCase())}
            />
          ))}
        </div>
      </div>

      {/* Feature Comparison Table */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <h2 className="text-3xl font-bold text-slate-900 text-center mb-8">
          Compare Plans
        </h2>
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-4 px-6 text-slate-600 font-medium">Feature</th>
                <th className="text-center py-4 px-3 text-slate-900 font-semibold text-sm">Essentials</th>
                <th className="text-center py-4 px-3 text-slate-900 font-semibold text-sm">Booking &amp; Directory</th>
                <th className="text-center py-4 px-3 text-df-gold font-semibold text-sm">Professional</th>
                <th className="text-center py-4 px-3 text-slate-900 font-semibold text-sm">Premier</th>
              </tr>
            </thead>
            <tbody>
              {comparisonFeatures.map((row, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="py-3 px-6 text-slate-700 text-sm">{row.feature}</td>
                  <td className="py-3 px-3 text-center text-sm text-slate-600">{row.essentials}</td>
                  <td className="py-3 px-3 text-center text-sm text-slate-600">{row.booking}</td>
                  <td className="py-3 px-3 text-center text-sm text-slate-900 font-medium">{row.professional}</td>
                  <td className="py-3 px-3 text-center text-sm text-slate-900 font-medium">{row.premier}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
          Frequently Asked Questions
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="font-semibold text-slate-900 mb-3">What's included in the free plan?</h3>
            <p className="text-slate-600">Essentials gives you a directory listing, a basic website, and up to 3 bookings per month — enough to get started and see if Divine Frameworks is right for you.</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="font-semibold text-slate-900 mb-3">How do payments work?</h3>
            <p className="text-slate-600">You connect your own Stripe account and keep 100% of what your clients pay. We don't take a cut of your bookings — ever. We only charge the monthly subscription for Professional and Premier.</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="font-semibold text-slate-900 mb-3">Can I upgrade or downgrade anytime?</h3>
            <p className="text-slate-600">Yes. Upgrade instantly to unlock features, or downgrade at the end of your billing cycle. No contracts, no cancellation fees.</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="font-semibold text-slate-900 mb-3">What are the AI features?</h3>
            <p className="text-slate-600">Professional includes AI Intelligent Dashboard, which helps you see patterns in your practice and clients who might need follow-up. Premier adds Session Prep (a personalized brief before each session) and Session Notes (automatic transcription and summaries).</p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Build Your Practice?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Join practitioners who are growing their businesses with Divine Frameworks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleGetStarted('free')}
              className="bg-white text-slate-900 px-8 py-4 rounded-xl font-semibold hover:bg-slate-100 transition-colors"
            >
              Start Free
            </button>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-slate-900 transition-colors"
            >
              View Features
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Pricing;
